import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, CheckCircle, AlertTriangle, Clock, DollarSign } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui';
import { GlassButton } from '@/design-system';
import type { AppDispatch, RootState } from '@/store';
import {
  validateRequest,
  clearValidation,
  selectValidationResult,
  selectValidationLoading,
} from '@/store/slices/resourceIntakeRequestsSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import type {
  ResourceIntakeRequest,
  RequestType,
  ResourceType,
  ValidationStatus,
  Program,
} from '@/types';
import { formatCurrencyFull, formatPercent } from '@/utils/formatters';

interface RequestFormProps {
  initialData?: ResourceIntakeRequest | null;
  onSubmit: (data: Partial<ResourceIntakeRequest>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const RequestForm: React.FC<RequestFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { entities: rateCards = [] } = useSelector((state: RootState) => state.rateCards);
  const { entities: programs = [] } = useSelector((state: RootState) => state.programs);
  const validationResult = useSelector(selectValidationResult);
  const validationLoading = useSelector(selectValidationLoading);

  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    requestType: 'HEADCOUNT' as RequestType,
    description: '',
    resourceType: 'EMPLOYEE' as ResourceType,
    resourceLevel: 'T3',
    estimatedCost: 0,
    businessJustification: '',
    programId: '' as string,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationDebounce, setValidationDebounce] = useState<NodeJS.Timeout | null>(null);

  // Load programs on mount
  useEffect(() => {
    dispatch(fetchPrograms({}));
    return () => {
      dispatch(clearValidation());
    };
  }, [dispatch]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        requestType: initialData.requestType,
        description: initialData.description,
        resourceType: initialData.resourceType,
        resourceLevel: initialData.resourceLevel?.code || 'T3',
        estimatedCost: initialData.estimatedCost || 0,
        businessJustification: initialData.businessJustification,
        programId: initialData.programId || '',
      });
    }
  }, [initialData]);

  // Auto-populate estimated cost from rate card
  useEffect(() => {
    const rateCard = rateCards.find((rc) => {
      // rc.resourceLevel may be a ResourceLevel object (from API) or a string code
      const levelCode =
        typeof rc.resourceLevel === 'object' && rc.resourceLevel !== null
          ? (rc.resourceLevel as { code: string }).code
          : String(rc.resourceLevel ?? rc.resourceLevelId);
      return levelCode === formData.resourceLevel && rc.year === currentYear;
    });
    if (rateCard) {
      // Estimate annual cost: hourly rate * 40 hours/week * 52 weeks
      const annualCost = rateCard.hourlyRate * 40 * 52;
      setFormData((prev) => ({ ...prev, estimatedCost: Math.round(annualCost) }));
    }
  }, [formData.resourceLevel, rateCards, currentYear]);

  // Debounced validation when program or resource level changes
  const triggerValidation = useCallback(() => {
    if (validationDebounce) {
      clearTimeout(validationDebounce);
    }
    const timeout = setTimeout(() => {
      if (formData.resourceLevel) {
        dispatch(
          validateRequest({
            resourceLevel: formData.resourceLevel,
            programId: formData.programId || undefined,
            businessJustification: formData.businessJustification,
          })
        );
      }
    }, 500);
    setValidationDebounce(timeout);
  }, [dispatch, formData.resourceLevel, formData.programId, formData.businessJustification]);

  useEffect(() => {
    triggerValidation();
    return () => {
      if (validationDebounce) {
        clearTimeout(validationDebounce);
      }
    };
  }, [formData.resourceLevel, formData.programId]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.requestType) {
      newErrors.requestType = 'Request type is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (!formData.resourceType) {
      newErrors.resourceType = 'Resource type is required';
    }

    if (!formData.resourceLevel) {
      newErrors.resourceLevel = 'Resource level is required';
    }

    if (!formData.businessJustification.trim()) {
      newErrors.businessJustification = 'Business justification is required';
    } else if (formData.businessJustification.trim().length < 20) {
      newErrors.businessJustification = 'Business justification must be at least 20 characters';
    } else if (formData.businessJustification.length > 1000) {
      newErrors.businessJustification = 'Business justification must be 1000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        programId: formData.programId || undefined,
      });
    }
  };

  const getValidationStatusColor = (status: ValidationStatus) => {
    switch (status) {
      case 'VALID':
        return 'bg-status-success/10 border-status-success/30 text-status-success';
      case 'WARNING':
        return 'bg-status-warning/10 border-status-warning/30 text-status-warning';
      case 'INVALID':
        return 'bg-status-error/10 border-status-error/30 text-status-error';
      case 'PENDING':
      default:
        return 'bg-accent-blue/10 border-accent-blue/30 text-accent-blue';
    }
  };

  const getValidationIcon = (status: ValidationStatus) => {
    switch (status) {
      case 'VALID':
        return <CheckCircle className="w-5 h-5" />;
      case 'WARNING':
        return <AlertTriangle className="w-5 h-5" />;
      case 'INVALID':
        return <AlertCircle className="w-5 h-5" />;
      case 'PENDING':
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-foreground">
          Request Type <span className="text-status-error">*</span>
        </label>
        <select
          className={`w-full h-9 px-3 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-all duration-200 ${
            errors.requestType ? 'border-status-error' : 'border-glass-border'
          }`}
          value={formData.requestType}
          onChange={(e) => handleInputChange('requestType', e.target.value)}
          disabled={!!initialData}
        >
          <option value="HEADCOUNT">Headcount (New Hire)</option>
          <option value="SOW">SOW (Statement of Work)</option>
          <option value="T_AND_M">T&M (Time and Materials)</option>
        </select>
        {errors.requestType && (
          <p className="text-xs text-status-error font-medium">{errors.requestType}</p>
        )}
      </div>

      {/* Program Selector */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-foreground">
          Program
        </label>
        <select
          className="w-full h-9 px-3 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue border-glass-border transition-all duration-200"
          value={formData.programId}
          onChange={(e) => handleInputChange('programId', e.target.value)}
        >
          <option value="">-- Select a program (optional for budget validation) --</option>
          {programs
            .filter((p) => p.isActive)
            .map((program) => (
              <option key={program.id} value={program.id}>
                {program.name}
              </option>
            ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Selecting a program enables budget validation
        </p>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-medium text-foreground">
            Description <span className="text-status-error">*</span>
          </label>
          <span className={`text-xs tabular-nums ${formData.description.length > 500 ? 'text-status-error font-medium' : 'text-muted-foreground'}`}>
            {formData.description.length}/500
          </span>
        </div>
        <textarea
          className={`w-full min-h-[72px] px-3 py-2 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-all duration-200 ${
            errors.description ? 'border-status-error' : 'border-glass-border'
          }`}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Brief description of the resource need..."
          rows={3}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground">Minimum 10 characters required</p>
        {errors.description && (
          <p className="text-xs text-status-error font-medium">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Resource Type <span className="text-status-error">*</span>
          </label>
          <select
            className={`w-full h-9 px-3 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-all duration-200 ${
              errors.resourceType ? 'border-status-error' : 'border-glass-border'
            }`}
            value={formData.resourceType}
            onChange={(e) => handleInputChange('resourceType', e.target.value)}
          >
            <option value="EMPLOYEE">Employee</option>
            <option value="CONTRACTOR">Contractor</option>
            <option value="GDP_ONSHORE">GDP Onshore</option>
            <option value="GDP_OFFSHORE">GDP Offshore</option>
            <option value="INTERN">Intern</option>
          </select>
          {errors.resourceType && (
            <p className="text-xs text-status-error font-medium">{errors.resourceType}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Resource Level <span className="text-status-error">*</span>
          </label>
          <select
            className={`w-full h-9 px-3 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-all duration-200 ${
              errors.resourceLevel ? 'border-status-error' : 'border-glass-border'
            }`}
            value={formData.resourceLevel}
            onChange={(e) => handleInputChange('resourceLevel', e.target.value)}
          >
            <optgroup label="Technical Levels">
              <option value="T1">T1 - Junior</option>
              <option value="T2">T2</option>
              <option value="T3">T3 - Mid-Level</option>
              <option value="T4">T4</option>
              <option value="T5">T5 - Senior</option>
              <option value="T6">T6 - Principal</option>
            </optgroup>
            <optgroup label="Management Levels">
              <option value="M2">M2 - Manager</option>
              <option value="M3">M3 - Senior Manager</option>
              <option value="M4">M4 - Director</option>
              <option value="M5">M5 - VP</option>
            </optgroup>
            <optgroup label="Other">
              <option value="GDP">GDP</option>
              <option value="CONTRACTOR">Contractor Level</option>
            </optgroup>
          </select>
          {errors.resourceLevel && (
            <p className="text-xs text-status-error font-medium">{errors.resourceLevel}</p>
          )}
        </div>
      </div>

      {/* Calculated Cost Display */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Calculated Annual Cost
          </label>
          <div className="flex items-center h-9 px-3 text-sm border rounded-lg bg-glass/60 backdrop-blur-sm border-glass-border shadow-ds-soft">
            <DollarSign className="w-4 h-4 text-muted-foreground mr-1" />
            <span className="font-medium text-foreground">
              {formatCurrencyFull(validationResult?.annualCost || formData.estimatedCost, { fallback: '$0' })}
            </span>
          </div>
          {validationResult?.hourlyRate && (
            <p className="text-xs text-muted-foreground">
              Based on ${validationResult.hourlyRate}/hr x 2,080 hrs/year
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Hourly Rate
          </label>
          <div className="flex items-center h-9 px-3 text-sm border rounded-lg bg-glass/60 backdrop-blur-sm border-glass-border shadow-ds-soft">
            <DollarSign className="w-4 h-4 text-muted-foreground mr-1" />
            <span className="font-medium text-foreground">
              {formatCurrencyFull(validationResult?.hourlyRate, { fallback: 'N/A' })}
            </span>
            <span className="text-muted-foreground ml-1">/hr</span>
          </div>
        </div>
      </div>

      {/* Validation Status Display */}
      {validationLoading ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-glass/60 backdrop-blur-sm border border-glass-border shadow-ds-soft">
          <LoadingSpinner size="sm" />
          <span className="text-sm text-muted-foreground">Validating budget impact...</span>
        </div>
      ) : validationResult ? (
        <div className={`rounded-md border p-3 ${getValidationStatusColor(validationResult.status)}`}>
          <div className="flex items-start gap-2">
            {getValidationIcon(validationResult.status)}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  {validationResult.status === 'VALID' && 'Budget Valid'}
                  {validationResult.status === 'WARNING' && 'Budget Warning'}
                  {validationResult.status === 'INVALID' && 'Budget Exceeded'}
                  {validationResult.status === 'PENDING' && 'Validation Pending'}
                </span>
                {validationResult.budgetImpactPercent !== null && (
                  <span className="text-xs bg-glass-subtle px-1.5 py-0.5 rounded">
                    {formatPercent(validationResult.budgetImpactPercent)} of remaining budget
                  </span>
                )}
              </div>
              <p className="text-xs mt-1 opacity-90">{validationResult.notes}</p>
              {validationResult.remainingBudgetBefore !== null && validationResult.remainingBudgetAfter !== null && (
                <div className="flex gap-4 mt-2 text-xs">
                  <span>
                    Before: {formatCurrencyFull(validationResult.remainingBudgetBefore)}
                  </span>
                  <span>
                    After: {formatCurrencyFull(validationResult.remainingBudgetAfter)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-md p-3">
          <p className="text-xs text-accent-blue">
            <strong>Note:</strong> After submission, your request will be reviewed by ADMIN or FINANCE users for approval.
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-medium text-foreground">
            Business Justification <span className="text-status-error">*</span>
          </label>
          <span className={`text-xs tabular-nums ${formData.businessJustification.length > 1000 ? 'text-status-error font-medium' : 'text-muted-foreground'}`}>
            {formData.businessJustification.length}/1000
          </span>
        </div>
        <textarea
          className={`w-full min-h-[96px] px-3 py-2 text-sm border rounded-lg bg-glass/80 backdrop-blur-sm text-foreground shadow-ds-soft focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-all duration-200 ${
            errors.businessJustification ? 'border-status-error' : 'border-glass-border'
          }`}
          value={formData.businessJustification}
          onChange={(e) => handleInputChange('businessJustification', e.target.value)}
          placeholder="Explain why this resource is needed and the expected business impact..."
          rows={4}
          maxLength={1000}
        />
        <p className="text-xs text-muted-foreground">Minimum 20 characters required</p>
        {errors.businessJustification && (
          <p className="text-xs text-status-error font-medium">{errors.businessJustification}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-border-primary">
        <GlassButton type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </GlassButton>
        <GlassButton type="submit" variant="primary" disabled={isLoading} isLoading={isLoading}>
          {initialData ? 'Update Request' : 'Submit Request'}
        </GlassButton>
      </div>
    </form>
  );
};

export default RequestForm;
